import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { TrialConfirmationDialog } from "@/components/trial/TrialConfirmationDialog";
import { TrialPricingDialog } from "@/components/trial/TrialPricingDialog";
import { TrialToolbarControl } from "@/components/trial/TrialToolbarControl";
import { useAuth } from "@/hooks/useAuth";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import type { IntelligencePlanId } from "@/lib/intelligencePlans";
import { captureTrialEvent } from "@/lib/trialAnalytics";
import type { TrialAccessRole } from "@/lib/trialAccess";
import { getLocalItem, setLocalItem } from "@/utils/safeStorage";

const ACKNOWLEDGMENT_PREFIX = "trial-confirmation-acknowledged-v1";
const EXPIRED_EVENT_PREFIX = "trial-expired-viewed-v1";

export function TrialExperience({
  role,
  compact = false,
  isPreview = false,
  mobileRow = false,
}: {
  role: TrialAccessRole;
  compact?: boolean;
  isPreview?: boolean;
  mobileRow?: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const trial = useTrialAccess({ role, isPreview });
  const [pricingOpen, setPricingOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const toolbarShownBoundaryRef = useRef<string | null>(null);
  const confirmationShownBoundaryRef = useRef<string | null>(null);
  const trialBoundary = trial.presentation.trialEndsAt ?? trial.presentation.state;
  const acknowledgmentKey = useMemo(
    () => user?.id
      ? `${ACKNOWLEDGMENT_PREFIX}:${user.id}:${trialBoundary}`
      : null,
    [trialBoundary, user?.id],
  );
  const canConfirm =
    trial.onboardingComplete &&
    (trial.presentation.state === "not_started" ||
      trial.presentation.state === "active" ||
      trial.presentation.state === "ending_soon" ||
      trial.presentation.state === "ends_today");
  const canShowToolbar = Boolean(trial.presentation.message && trial.presentation.cta);

  useEffect(() => {
    if (!acknowledgmentKey || !canConfirm || getLocalItem(acknowledgmentKey) === "true") return;
    setConfirmationOpen(true);
    if (confirmationShownBoundaryRef.current === acknowledgmentKey) return;
    confirmationShownBoundaryRef.current = acknowledgmentKey;
    captureTrialEvent("trial_confirmation_shown", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface: `${role}_post_onboarding`,
    });
  }, [acknowledgmentKey, canConfirm, role, trial.presentation.daysRemaining, trial.presentation.state]);

  useEffect(() => {
    const boundary = canShowToolbar ? `${role}:${trialBoundary}` : null;
    if (!boundary || toolbarShownBoundaryRef.current === boundary) return;
    toolbarShownBoundaryRef.current = boundary;
    captureTrialEvent("trial_toolbar_shown", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface: `${role}_toolbar`,
    });
  }, [canShowToolbar, role, trial.presentation.daysRemaining, trial.presentation.state, trialBoundary]);

  useEffect(() => {
    if (!user?.id || trial.presentation.state !== "expired") return;
    const eventKey = `${EXPIRED_EVENT_PREFIX}:${user.id}:${trialBoundary}`;
    if (getLocalItem(eventKey) === "true") return;
    captureTrialEvent("trial_expired_viewed", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface: `${role}_app`,
    });
    setLocalItem(eventKey, "true");
  }, [role, trial.presentation.daysRemaining, trial.presentation.state, trialBoundary, user?.id]);

  const acknowledgeConfirmation = () => {
    if (acknowledgmentKey) setLocalItem(acknowledgmentKey, "true");
    setConfirmationOpen(false);
    captureTrialEvent("trial_confirmation_completed", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface: `${role}_post_onboarding`,
    });
  };

  const exploreValue = () => {
    captureTrialEvent("trial_toolbar_clicked", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface: `${role}_toolbar_value`,
    });
    if (role === "coach") {
      navigate(trial.primaryLeaderId ? `/coach/leaders/${trial.primaryLeaderId}` : "/coach/overview");
      return;
    }
    navigate("/today");
  };

  const openPricing = (surface: string) => {
    setPricingOpen(true);
    captureTrialEvent("trial_pricing_opened", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface,
    });
  };

  const completeConfirmation = () => {
    acknowledgeConfirmation();
    exploreValue();
  };

  const selectPlan = (planId: IntelligencePlanId) => {
    captureTrialEvent("trial_plan_selected", {
      role,
      trialState: trial.presentation.state,
      daysRemaining: trial.presentation.daysRemaining,
      surface: `${role}_trial_pricing`,
      planId,
    });
    setPricingOpen(false);
    if (role === "coach") {
      const params = new URLSearchParams({ trial_plan: planId });
      if (trial.primaryLeaderId) params.set("trial_leader", trial.primaryLeaderId);
      navigate(`/coach/settings?${params.toString()}#leaders`);
      return;
    }
    navigate(planId === "intelligence_solo" ? "/settings#plan-intelligence" : "/settings#billing");
  };

  return (
    <>
      {canShowToolbar ? (
        <div className={mobileRow ? "flex border-b border-border bg-background px-4 py-2" : undefined}>
          <TrialToolbarControl
            trial={trial}
            compact={compact}
            onExplore={exploreValue}
            onOpenPricing={() => {
              captureTrialEvent("trial_toolbar_clicked", {
                role,
                trialState: trial.presentation.state,
                daysRemaining: trial.presentation.daysRemaining,
                surface: `${role}_toolbar_pricing`,
              });
              openPricing(`${role}_toolbar`);
            }}
          />
        </div>
      ) : null}
      <TrialConfirmationDialog
        trial={trial}
        open={confirmationOpen}
        onDismiss={acknowledgeConfirmation}
        onComplete={completeConfirmation}
        onViewPricing={() => {
          acknowledgeConfirmation();
          openPricing(`${role}_post_onboarding`);
        }}
      />
      <TrialPricingDialog
        role={role}
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        primaryLeaderId={trial.primaryLeaderId}
        onSelectPlan={selectPlan}
      />
    </>
  );
}
